import React, { useCallback, useEffect, useState } from 'react';

import { LinkButton } from 'src/app/components/lib/button';
import { WizardCardList } from 'src/app/components/lib/wizard/card';
import { FullWidthWizardCard } from 'src/app/components/lib/wizard/card/full-width-card';

import { Filter } from '../../shared/filter';
import { getRequirementsFromRequirementStepState, isRequirementsStepValid } from '../../shared/helpers';
import { useDiscountWizardStateContext } from '../../state';
import { RequirementsStepActions } from '../../state/requirements';
import { ThresholdType } from '../../types';

import { RequirementCard } from './requirement-card';
import { RequirementCardList } from './styled';

import type { WizardStepComponentProps } from 'src/app/components/lib/wizard';

export function Requirements({
  enableNextButton,
  disableNextButton,
  setComplete,
  setIncomplete,
  stepStatus,
  setSubstepActive,
  substepActive,
}: WizardStepComponentProps) {
  const { isManualDiscount, requirementsStepState, requirementsStepDispatch } = useDiscountWizardStateContext();
  const requirements = getRequirementsFromRequirementStepState(requirementsStepState);
  const requirementKeys = Object.keys(requirements);

  const [filteringRequirement, setFilteringRequirement] = useState<number | undefined>(undefined);

  const markStepComplete = useCallback(() => {
    enableNextButton();
    setComplete();
  }, [enableNextButton, setComplete]);

  const markStepIncomplete = useCallback(() => {
    disableNextButton();
    setIncomplete();
  }, [disableNextButton, setIncomplete]);

  useEffect(() => {
    const stepIsValid = isRequirementsStepValid(requirementsStepState);
    if (!stepIsValid && stepStatus.complete) {
      markStepIncomplete();
    }
    if (stepIsValid && !stepStatus.complete) {
      markStepComplete();
    }
  }, [enableNextButton, markStepComplete, markStepIncomplete, requirementsStepState, setComplete, stepStatus]);

  useEffect(() => {
    requirementsStepDispatch({ type: RequirementsStepActions.VISITED }, true);
  }, [requirementsStepDispatch]);

  // If screen is really wide, allow the content of the wizard to grow so there's less dead space - 1440px comes from the breakpoint in our design system for wide desktops (src/app/components/lib/global-styles/breakpoints.ts)
  const screenSizeAboveLargeBreakpoint = window.screen.availWidth > 1440;

  return (
    <>
      {substepActive && filteringRequirement && <Filter requirement={filteringRequirement} />}
      {!substepActive && (
        <WizardCardList $maxWidth={screenSizeAboveLargeBreakpoint ? '1000px' : '850px'}>
          <FullWidthWizardCard
            description='Specify the products and conditions the customer must fulfill to apply the discount.'
            title='Requirements'
          >
            <RequirementCardList>
              {requirementKeys.map((requirement) => (
                <RequirementCard
                  key={`requirement-${requirement}`}
                  requirement={Number(requirement)}
                  onEditFiltersClick={() => {
                    setSubstepActive();
                    setFilteringRequirement(Number(requirement));
                  }}
                />
              ))}
              {!isManualDiscount && (
                <LinkButton
                  label='+ Add another requirement'
                  onClick={() => {
                    requirementsStepDispatch({ type: RequirementsStepActions.ADD_REQUIREMENT });
                    if (requirements[1]?.condition === ThresholdType.NoThreshold) {
                      requirementsStepDispatch({
                        type: RequirementsStepActions.SET_REQUIREMENT_CONDITION,
                        payload: {
                          index: 1,
                          value: ThresholdType.NumberOfItems,
                        },
                      });
                    }
                  }}
                />
              )}
            </RequirementCardList>
          </FullWidthWizardCard>
        </WizardCardList>
      )}
    </>
  );
}
